import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { feedbackApi } from '../api/endpoints.ts';
import { useDebouncedValue, useDocumentTitle } from '../hooks/index.ts';
import { Alert, Button, LoadingBlock, Pagination, Select, TextArea, TextInput, classes } from '../components/ui.tsx';
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_SORTS,
  FEEDBACK_TABS,
  FEEDBACK_TITLE_MAX,
  FEEDBACK_DESCRIPTION_MAX,
} from '@shared/constants/feedback.ts';
import type { FeedbackCategory, FeedbackSort, FeedbackTab } from '@shared/constants/feedback.ts';
import type { FeedbackRequestSummary, FeedbackSimilarRequest } from '@shared/types/index.ts';
import { formatShortInstant } from '../i18n/datetime.ts';

/**
 * Help Shape BlastoClean — the customer's feedback board (spec §6, §42).
 *
 * Deliberately a list with four tabs rather than a board with columns. The
 * whole premise of this feature is that a cleaner opens it, searches, votes,
 * and leaves; anything that reads as a project-management tool is a thing they
 * will not use.
 *
 * The quota line under the button is the one piece of UI that has to be got
 * right (§43). It never says "you are out" without also saying what somebody
 * CAN do — vote now, or submit next week — because a limit that reads as a
 * punishment produces exactly one behavior, which is that people stop coming.
 */
export default function FeedbackPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('feedback.documentTitle'));
  const queryClient = useQueryClient();

  const [params, setParams] = useSearchParams();
  const tab = (FEEDBACK_TABS as readonly string[]).includes(params.get('tab') ?? '')
    ? (params.get('tab') as FeedbackTab)
    : 'ideas';
  const sort = (FEEDBACK_SORTS as readonly string[]).includes(params.get('sort') ?? '')
    ? (params.get('sort') as FeedbackSort)
    : 'trending';
  const category = (FEEDBACK_CATEGORIES as readonly string[]).includes(params.get('category') ?? '')
    ? (params.get('category') as FeedbackCategory)
    : undefined;
  const view = params.get('view');
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  const [search, setSearch] = useState(params.get('q') ?? '');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [error, setError] = useState<string | null>(null);

  const setParam = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(updates)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    // Any change to what is being listed puts the reader back on page one;
    // page 4 of a different filter is somebody else's page 4.
    if (!('page' in updates)) next.delete('page');
    setParams(next, { replace: true });
  };

  const state = useQuery({ queryKey: ['feedback-state'], queryFn: () => feedbackApi.state() });

  const board = useQuery({
    queryKey: ['feedback', { tab, sort, category, q: debouncedSearch, page }],
    queryFn: () =>
      feedbackApi.list({
        tab,
        sort,
        ...(category ? { category } : {}),
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
        page,
        pageSize: 20,
      }),
    enabled: view !== 'mine',
  });

  const mine = useQuery({
    queryKey: ['feedback-mine'],
    queryFn: () => feedbackApi.mine(),
    enabled: view === 'mine',
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['feedback'] });
    void queryClient.invalidateQueries({ queryKey: ['feedback-mine'] });
    void queryClient.invalidateQueries({ queryKey: ['feedback-state'] });
  };

  const vote = useMutation({
    mutationFn: ({ id, voted }: { id: string; voted: boolean }) =>
      voted ? feedbackApi.unvote(id) : feedbackApi.vote(id),
    onSuccess: invalidate,
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  const eligibility = state.data?.eligibility ?? null;

  // A retry without cached state must keep an already opened draft mounted.
  if (state.isLoading && !state.isFetched)
    return (
      <WorkPage title={t('feedback.title')} description={t('feedback.description')}>
        <div className="card">
          <LoadingBlock label={t('feedback.loading')} />
        </div>
      </WorkPage>
    );
  if (state.data && !state.data.enabled) {
    return (
      <WorkPage title={t('feedback.title')}>
        <WorkEmpty title={t('feedback.disabledTitle')}>{t('feedback.disabledBody')}</WorkEmpty>
      </WorkPage>
    );
  }

  const items = view === 'mine' ? (mine.data?.items ?? []) : (board.data?.items ?? []);
  const listQuery = view === 'mine' ? mine : board;
  const isLoading = state.isLoading || (view === 'mine' ? mine.isLoading : board.isLoading);

  return (
    <WorkPage
      title={t('feedback.title')}
      description={t('feedback.description')}
      actions={
        <>
          <Button variant="primary" onClick={() => setParam({ view: 'new' })}>
            {t('feedback.suggestIdea')}
          </Button>
          <Button onClick={() => setParam({ view: view === 'mine' ? undefined : 'mine' })}>
            {view === 'mine' ? t('feedback.backToBoard') : t('feedback.myFeedback')}
          </Button>
        </>
      }
    >
      {error ? (
        <div className="mb-4">
          <Alert tone="error" title={t('feedback.actionErrorTitle')}>
            {error}
          </Alert>
        </div>
      ) : null}

      <NotificationPanel onChanged={invalidate} />

      {view === 'new' ? (
        <SubmissionFlow
          onClose={() => setParam({ view: undefined })}
          onSubmitted={() => {
            invalidate();
            setParam({ view: 'mine' });
          }}
          onVoted={invalidate}
        />
      ) : null}

      {view !== 'new' && eligibility ? (
        <QuotaLine eligibility={eligibility} onBrowse={() => setParam({ view: undefined, tab: 'ideas' })} />
      ) : null}

      {view === 'mine' ? null : (
        <>
          <nav
            className="mb-4 flex flex-wrap gap-1 border-b border-[color:var(--color-line)]"
            aria-label={t('feedback.tabsAriaLabel')}
          >
            {FEEDBACK_TABS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setParam({ tab: value })}
                aria-current={tab === value ? 'page' : undefined}
                className={classes(
                  '-mb-px border-b-2 px-4 py-2 text-sm font-semibold',
                  tab === value
                    ? 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]'
                    : 'border-transparent text-[color:var(--color-muted)]',
                )}
              >
                {t(`feedback.tab.${value}`)}
              </button>
            ))}
          </nav>

          <div className="card mb-6 grid gap-4 sm:grid-cols-3">
            <TextInput
              label={t('feedback.searchLabel')}
              value={search}
              placeholder={t('feedback.searchPlaceholder')}
              onChange={(event) => {
                setSearch(event.target.value);
                setParam({ q: event.target.value || undefined });
              }}
            />
            <Select
              label={t('feedback.sortLabel')}
              value={sort}
              options={FEEDBACK_SORTS.map((value) => ({ value, label: t(`feedback.sort.${value}`) }))}
              onChange={(event) => setParam({ sort: event.target.value })}
            />
            <Select
              label={t('feedback.categoryLabel')}
              value={category ?? ''}
              options={[
                { value: '', label: t('feedback.categoryAll') },
                ...FEEDBACK_CATEGORIES.map((value) => ({ value, label: t(`feedback.category.${value}`) })),
              ]}
              onChange={(event) => setParam({ category: event.target.value || undefined })}
            />
          </div>
        </>
      )}

      <WorkSection title={view === 'mine' ? t('feedback.myFeedback') : t(`feedback.tab.${tab}`)}>
        {state.isError || listQuery.isError ? (
          <Alert tone="error" title={t('feedback.actionErrorTitle')}>
            <p>{((state.error ?? listQuery.error) as Error).message}</p>
            <Button
              variant="secondary"
              onClick={() => {
                void state.refetch();
                void listQuery.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          </Alert>
        ) : null}
        {isLoading ? <LoadingBlock label={t('feedback.loading')} /> : null}

        {!isLoading && !state.isError && !listQuery.isError && items.length === 0 ? (
          <WorkEmpty title={view === 'mine' ? t('feedback.emptyMineTitle') : t('feedback.emptyTitle')}>
            {view === 'mine' ? t('feedback.emptyMineBody') : t('feedback.emptyBody')}
            {view !== 'mine' && (search || category) ? (
              <p className="mt-3">
                <Button
                  onClick={() => {
                    setSearch('');
                    setParam({ q: undefined, category: undefined });
                  }}
                >
                  {t('feedback.backToBoard')}
                </Button>
              </p>
            ) : null}
          </WorkEmpty>
        ) : null}

        <ul className="grid gap-3">
          {items.map((item) => (
            <li key={item.id}>
              <RequestCard
                request={item}
                busy={vote.isPending}
                onVote={() => vote.mutate({ id: item.id, voted: item.hasVoted })}
              />
            </li>
          ))}
        </ul>

        {view === 'mine' ? null : (
          <Pagination
            page={page}
            totalPages={board.data?.totalPages}
            onPageChange={(next) => setParam({ page: String(next) })}
          />
        )}
      </WorkSection>
    </WorkPage>
  );
}

// ---------------------------------------------------------------------------
// Lifecycle updates (§29)
// ---------------------------------------------------------------------------

/**
 * What happened to the ideas this person submitted or voted for.
 *
 * The in-app half of the notification channel: the same rows the cron pass
 * mails, shown here the moment they are written. That matters most on a
 * deployment with no mail sender configured, where this is the only way a
 * customer ever learns their idea shipped.
 *
 * Renders nothing when there is nothing to say — no empty "no notifications"
 * card on a board most people open to vote and leave. Unread ones are marked
 * read on the way out rather than on arrival, so opening the page does not
 * silently clear a badge somebody has not looked at yet.
 */
function NotificationPanel({ onChanged }: { onChanged: () => void }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const query = useQuery({ queryKey: ['feedback-notifications'], queryFn: () => feedbackApi.notifications() });

  const markRead = useMutation({
    mutationFn: () => feedbackApi.markNotificationsRead(),
    onSuccess: () => {
      void query.refetch();
      onChanged();
    },
  });

  const items = query.data?.items ?? [];
  if (items.length === 0) return null;

  const unread = query.data?.unread ?? 0;
  const total = query.data?.total ?? items.length;
  const shown = expanded ? items : items.slice(0, 3);

  return (
    <section className="card mb-6" aria-labelledby="feedback-updates-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="feedback-updates-heading" className="text-lg font-semibold">
          {unread > 0 ? t('feedback.updatesTitleUnread', { count: unread }) : t('feedback.updatesTitle')}
        </h2>
        {unread > 0 ? (
          <Button disabled={markRead.isPending} onClick={() => markRead.mutate()}>
            {t('feedback.markAllRead')}
          </Button>
        ) : null}
      </div>

      <ul className="mt-4 grid gap-3">
        {shown.map((item) => (
          <li key={item.id} className="flex gap-3">
            <span
              aria-hidden="true"
              className={classes(
                'mt-2 h-2 w-2 shrink-0 rounded-full',
                item.readAt === null ? 'bg-[color:var(--color-brand)]' : 'bg-[color:var(--color-line)]',
              )}
            />
            <div className="min-w-0">
              <Link to={`/feedback/${item.requestId}`} className="font-medium hover:underline">
                {item.title}
              </Link>
              {/* Announced, because the dot beside it is decorative. */}
              {item.readAt === null ? <span className="sr-only">{t('feedback.unreadSr')}</span> : null}
              {item.body ? <p className="mt-1 text-sm text-[color:var(--color-muted)]">{item.body}</p> : null}
              <p className="text-xs text-[color:var(--color-muted)]">{formatShortInstant(item.createdAt)}</p>
            </div>
          </li>
        ))}
      </ul>

      {items.length > 3 ? (
        <p className="mt-3">
          <Button onClick={() => setExpanded((current) => !current)}>
            {expanded ? t('feedback.showFewerUpdates') : t('feedback.showAllUpdates', { count: items.length })}
          </Button>
        </p>
      ) : null}

      {/* Said outright rather than left to be inferred from a short list: the
          badge counts everything, so a panel that silently showed one page
          would have the two disagreeing with no explanation. */}
      {total > items.length ? (
        <p className="mt-3 text-xs text-[color:var(--color-muted)]">
          {t('feedback.updatesTruncated', { shown: items.length, total })}
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The quota line (§43)
// ---------------------------------------------------------------------------

function QuotaLine({
  eligibility,
  onBrowse,
}: {
  eligibility: NonNullable<Awaited<ReturnType<typeof feedbackApi.state>>['eligibility']>;
  onBrowse: () => void;
}) {
  const { t } = useTranslation();

  if (eligibility.reason === 'monthly_vote_required') {
    return (
      <div className="mb-6">
        <Alert tone="info" title={t('feedback.voteRequiredTitle')}>
          <p>{t('feedback.voteRequiredBody')}</p>
          <p className="mt-3">
            <Button onClick={onBrowse}>{t('feedback.browseToVote')}</Button>
          </p>
        </Alert>
      </div>
    );
  }

  if (eligibility.reason === 'weekly_limit_reached') {
    return (
      <p className="mb-6 text-sm text-[color:var(--color-muted)]">
        {t('feedback.weeklyExhausted', { limit: eligibility.weeklyLimit })}
      </p>
    );
  }

  if (eligibility.reason === 'permission' || eligibility.reason === 'feature_disabled') return null;

  return (
    <p className="mb-6 text-sm text-[color:var(--color-muted)]">
      {/* `count`, not `remaining`: a plural key is selected by `count` and by
          nothing else, so passing the number under any other name leaves
          i18next looking for a bare `feedback.weeklyRemaining` that does not
          exist — and printing the key at the customer. */}
      {t('feedback.weeklyRemaining', { count: eligibility.weeklyRemaining })}
    </p>
  );
}

// ---------------------------------------------------------------------------
// One idea
// ---------------------------------------------------------------------------

function RequestCard({
  request,
  busy,
  onVote,
}: {
  request: FeedbackRequestSummary;
  busy: boolean;
  onVote: () => void;
}) {
  const { t } = useTranslation();

  return (
    <article className="card flex gap-4">
      {/* The vote control first in the source as well as on screen: it is the
          one thing on this card most readers came to press. */}
      <div className="shrink-0">
        <button
          type="button"
          onClick={onVote}
          disabled={busy || request.status === 'deployed' || request.status === 'pending_moderation'}
          aria-pressed={request.hasVoted}
          className={classes(
            'flex w-20 flex-col items-center rounded-lg border px-2 py-3 text-sm font-semibold transition',
            request.hasVoted
              ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand)]'
              : 'border-[color:var(--color-line)]',
            busy ? 'opacity-60' : '',
          )}
        >
          <span aria-hidden="true">{request.hasVoted ? '✓' : '▲'}</span>
          <span className="text-lg">{request.votes}</span>
          <span className="text-xs font-normal">{request.hasVoted ? t('feedback.voted') : t('feedback.vote')}</span>
        </button>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/feedback/${request.id}`} className="text-base font-semibold hover:underline">
            {request.title}
          </Link>
          <StatusPill status={request.customerStatus} />
          {request.previewAvailable ? (
            <span className="badge border-[color:var(--color-brand)] text-[color:var(--color-brand)]">
              {t('feedback.previewBadge')}
            </span>
          ) : null}
          {request.submittedByYourCompany ? (
            <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
              {t('feedback.yourIdea')}
            </span>
          ) : null}
        </div>

        <p className="mt-2 line-clamp-3 text-sm text-[color:var(--color-muted)]">{request.description}</p>

        {request.publicAdminResponse ? (
          <p className="mt-3 rounded-md border-l-4 border-[color:var(--color-brand)] bg-[color:var(--color-surface)] p-3 text-sm">
            {request.publicAdminResponse}
          </p>
        ) : null}

        {request.status === 'deployed' && request.releaseNotes ? (
          <p className="mt-3 text-sm">{request.releaseNotes}</p>
        ) : null}

        <p className="mt-3 text-xs text-[color:var(--color-muted)]">
          {request.category ? <>{t(`feedback.category.${request.category}`)} · </> : null}
          {request.status === 'deployed' && request.deployedAt
            ? t('feedback.releasedOn', { date: formatShortInstant(request.deployedAt) })
            : t('feedback.submittedOn', { date: formatShortInstant(request.createdAt) })}
          {/* Never the company's name (§6) — only that a customer asked. */}
          {request.submittedByYourCompany ? null : <> · {t('feedback.submittedByCustomer')}</>}
        </p>
      </div>
    </article>
  );
}

export function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  // Color reinforces the label, never carries it — the same rule the quote
  // status badge follows.
  const tone =
    status === 'completed'
      ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
      : status === 'in_progress' || status === 'preview'
        ? 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]'
        : status === 'rejected'
          ? 'border-[color:var(--color-line)] text-[color:var(--color-muted)]'
          : 'border-[color:var(--color-line)] text-[color:var(--color-muted)]';
  return <span className={classes('badge', tone)}>{t(`feedback.status.${status}`)}</span>;
}

// ---------------------------------------------------------------------------
// Submission (§8, §9, §10)
// ---------------------------------------------------------------------------

/**
 * The two-step submission flow.
 *
 * Step one is a title box that searches while somebody types, because the
 * cheapest duplicate to prevent is the one that was never written (§8). The
 * description box only appears once they have seen what already exists —
 * deliberately, so "vote instead" is offered before they have invested five
 * minutes in prose.
 */
function SubmissionFlow({
  onClose,
  onSubmitted,
  onVoted,
}: {
  onClose: () => void;
  onSubmitted: () => void;
  onVoted: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const debouncedTitle = useDebouncedValue(title, 350);

  const similar = useQuery({
    queryKey: ['feedback-similar', debouncedTitle],
    queryFn: () => feedbackApi.similar(debouncedTitle),
    enabled: debouncedTitle.trim().length >= 3,
  });

  const vote = useMutation({
    mutationFn: (id: string) => feedbackApi.vote(id),
    onSuccess: () => {
      onVoted();
      void similar.refetch();
    },
  });

  const submit = useMutation({
    mutationFn: () =>
      feedbackApi.submit({
        title: title.trim(),
        description: description.trim(),
        ...(category ? { category: category as FeedbackCategory } : {}),
      }),
    onSuccess: (result) => {
      setSubmitted(result.moderationRequired);
      if (!result.moderationRequired) onSubmitted();
    },
    onError: (mutationError: Error & { fields?: Record<string, string> }) => {
      setError(mutationError.message);
      setFields(mutationError.fields ?? {});
    },
  });

  // Somebody who has typed enough for a real title has passed the point where
  // hiding the description box helps them.
  useEffect(() => {
    if (title.trim().length >= 12) setExpanded(true);
  }, [title]);

  if (submitted) {
    return (
      <div className="card mb-6">
        <Alert tone="success" title={t('feedback.submittedTitle')}>
          <p>{t('feedback.submittedBody')}</p>
          <ul className="mt-2 list-disc pl-5 text-sm">
            <li>{t('feedback.submittedReasonSpam')}</li>
            <li>{t('feedback.submittedReasonDuplicate')}</li>
            <li>{t('feedback.submittedReasonSupport')}</li>
            <li>{t('feedback.submittedReasonPrivacy')}</li>
          </ul>
          <p className="mt-3">
            <Button onClick={onSubmitted}>{t('feedback.viewMyFeedback')}</Button>
          </p>
        </Alert>
      </div>
    );
  }

  return (
    <form
      className="card mb-6 grid gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        setFields({});
        submit.mutate();
      }}
    >
      <h2 className="text-lg font-semibold">{t('feedback.formTitle')}</h2>

      {error ? (
        <Alert tone="error" title={t('feedback.formErrorTitle')}>
          {error}
        </Alert>
      ) : null}

      <TextInput
        label={t('feedback.titleLabel')}
        hint={t('feedback.titleHint')}
        error={fields.title}
        required
        value={title}
        maxLength={FEEDBACK_TITLE_MAX}
        placeholder={t('feedback.titlePlaceholder')}
        onChange={(event) => setTitle(event.target.value)}
      />

      {similar.data && similar.data.items.length > 0 ? (
        <SimilarPanel items={similar.data.items} busy={vote.isPending} onVote={(id) => vote.mutate(id)} />
      ) : null}

      {expanded ? (
        <>
          <TextArea
            label={t('feedback.descriptionLabel')}
            hint={t('feedback.descriptionHint')}
            error={fields.description}
            required
            value={description}
            rows={6}
            maxLength={FEEDBACK_DESCRIPTION_MAX}
            placeholder={t('feedback.descriptionPlaceholder')}
            onChange={(event) => setDescription(event.target.value)}
          />

          <Select
            label={t('feedback.categoryOptionalLabel')}
            value={category}
            options={[
              { value: '', label: t('feedback.categoryNone') },
              ...FEEDBACK_CATEGORIES.map((value) => ({ value, label: t(`feedback.category.${value}`) })),
            ]}
            onChange={(event) => setCategory(event.target.value)}
          />
        </>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={submit.isPending || !expanded}>
          {submit.isPending ? t('feedback.submitting') : t('feedback.submit')}
        </Button>
        <Button type="button" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

function SimilarPanel({
  items,
  busy,
  onVote,
}: {
  items: FeedbackSimilarRequest[];
  busy: boolean;
  onVote: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4">
      <p className="font-semibold">{t('feedback.similarTitle')}</p>
      <ul className="mt-3 grid gap-2">
        {items.map((item) => (
          <li key={item.id} className="flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0">
              <Link to={`/feedback/${item.id}`} className="font-medium hover:underline">
                {item.title}
              </Link>
              <span className="ml-2 text-sm text-[color:var(--color-muted)]">
                {t('feedback.voteCount', { count: item.votes })}
              </span>
            </span>
            <Button type="button" disabled={busy || item.hasVoted} onClick={() => onVote(item.id)}>
              {item.hasVoted ? t('feedback.voted') : t('feedback.voteInstead')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
